import { apiGet, apiPatch, apiPost } from '../client';
import type {
  LoginResult,
  MfaEnabled,
  MfaSetup,
  MfaStatus,
  RegisterPayload,
  Role,
  TokenResponse,
  User,
  UserUpdatePayload,
} from '@/types/api';

/**
 * Login is OAuth2 password flow, so it is form-encoded and the email travels in a
 * field called `username`. Everything else on this router is JSON.
 */
export function login(email: string, password: string): Promise<LoginResult> {
  return apiPost<LoginResult>('/auth/login', {
    form: { username: email, password },
    anonymous: true,
  });
}

/** Always creates a vehicle_owner; the schema has no role field on purpose. */
export function register(payload: RegisterPayload): Promise<User> {
  return apiPost<User>('/auth/register', { body: payload, anonymous: true });
}

/**
 * The single point where the app learns who it is signed in as. The token carries
 * only an email and an expiry, so without this the UI cannot know the user's role.
 */
export function fetchCurrentUser(): Promise<User> {
  return apiGet<User>('/auth/me');
}

export function updateProfile(payload: UserUpdatePayload): Promise<User> {
  return apiPatch<User>('/auth/users/me', { body: payload });
}

/** Administrator only. Roles are granted, never self-assigned. */
export function setUserRole(userId: number, role: Role): Promise<User> {
  return apiPatch<User>(`/auth/users/${userId}/role`, { body: { role } });
}

/** Administrator only. The directory behind the role picker. */
export function listUsers(role?: Role): Promise<User[]> {
  return apiGet<User[]>('/auth/users', { query: { role } });
}


/* -------------------------------------------------------------------------- */
/* Two-factor (TOTP / Google Authenticator)                                    */
/* -------------------------------------------------------------------------- */

/**
 * These three take the challenge token from /auth/login, not a session — the whole
 * point is that no session exists until the second factor is satisfied.
 */

export function mfaSetup(challengeToken: string): Promise<MfaSetup> {
  return apiPost<MfaSetup>('/auth/2fa/setup', {
    body: { challenge_token: challengeToken },
    anonymous: true,
  });
}

export function mfaEnable(challengeToken: string, code: string): Promise<MfaEnabled> {
  return apiPost<MfaEnabled>('/auth/2fa/enable', {
    body: { challenge_token: challengeToken, code },
    anonymous: true,
  });
}

export function mfaVerify(challengeToken: string, code: string): Promise<TokenResponse> {
  return apiPost<TokenResponse>('/auth/2fa/verify', {
    body: { challenge_token: challengeToken, code },
    anonymous: true,
  });
}

export function mfaStatus(): Promise<MfaStatus> {
  return apiGet<MfaStatus>('/auth/2fa/status');
}

/** Administrator only. Clears someone else's enrollment after a lost phone. */
export function mfaReset(userId: number): Promise<User> {
  return apiPost<User>(`/auth/2fa/reset/${userId}`);
}

/* -------------------------------------------------------------------------- */
/* Password reset                                                              */
/* -------------------------------------------------------------------------- */

/** Always succeeds, whether or not the address has an account. Do not branch on it. */
export function requestPasswordReset(email: string): Promise<void> {
  return apiPost<void>('/auth/password-reset/request', {
    body: { email },
    anonymous: true,
  });
}

export function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  return apiPost<void>('/auth/password-reset/confirm', {
    body: { token, new_password: newPassword },
    anonymous: true,
  });
}
